import { Theme, useSession, useTranslation } from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import { Logger, type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  profileApi,
  RegistrationResponse,
  RenewalNudgeType,
  updateAccountAutoRenewalData,
  Subscription,
  CartAccountProcess,
  ContactType,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import {
  Amount,
  CreatePaymentRequest,
  CreateTransactionRequest,
  PaymentApi,
  PaymentDetails,
  RefType,
} from '@commerce-payment-webcomponents/sdk';
import logObject from '../../logObject';
import { parseString, parseNumber, parseArray, siteCountry, iff, parseDate } from './helper';
import { useCartConfig, useMyAccountProfileConfig } from './masterdata';
import { AccountLifeCycleConstants, ErrorConstants, AccountLifeCycleKitType, AccountTypeCode } from './Constants';
import { handleAPIResponseError } from './profileUtils';
import { useLocale } from './customHooks';
import {
  accountRenewalBannerStatus,
  getAccountAutoRenewal,
  autoRenewalInitFeeData,
  autoRenewalGetExistingRegistration,
  autoRenewalTransformToSubscription,
  autoRenewalTransformFromSubscription,
  autoRenewalStartProcess,
  autoRenewalGetDefaultAddress,
  autoRenewalValidatePaymentDetails,
} from './accountLifeCycleUtils';
import { getConfigByPK, getInputConfig, MasterConfigNameConst } from './myAccountProfileUtils';
import { useGlobalErrorHandler } from './globalErrorHook';
import { useFeatureFlag } from './featureFlagHook';

export const useAutoRenewal = () => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { isABO, session } = useSession();
  const { id, aboNum, accountExpirationDate, type } = session;
  const profileId = parseString(id);
  const aboNumber = parseString(aboNum);
  const apiErrorHandler = useGlobalErrorHandler();
  const featureFlags = useFeatureFlag();

  const accountAutoRenewalData = useSelector((state: AppState) => state.commerceProfile.accountAutoRenewalData);
  const { selectedFeeSku } = accountAutoRenewalData;

  const refType = RefType.AutoRenewal;

  // renewal config
  const myAccountConfig = useMyAccountProfileConfig();
  const { inputs: lifeCycleConfig } = getConfigByPK(myAccountConfig, MasterConfigNameConst.STEP_ACCOUNT_LIFE_CYCLE);
  const autoRenewalConfig = getInputConfig(lifeCycleConfig, AccountLifeCycleConstants.ALLOW_AUTO_RENEWAL);

  const reuseRegistration = Boolean(autoRenewalConfig?.reuseRegistration);
  const allowInitiateFromCheckout = Boolean(autoRenewalConfig?.allowInitiateFromCheckout);
  const authorizationCharge = parseNumber(autoRenewalConfig?.authorizationCharge, 1);
  const autoRenewProcessDate = parseNumber(autoRenewalConfig?.autoRenewProcessDate, 1);
  const autoRenewSubscriptionId = parseString(autoRenewalConfig?.autoRenewSubscriptionId, '1');
  const autoRenewSubscriptionName = parseString(autoRenewalConfig?.autoRenewSubscriptionName, 'Auto Renewal');
  const defaultPickupType = parseString(autoRenewalConfig?.defaultPickupType, 'bopis'); // Default pickup type for auto renewal

  const { enableAllowRenewOnAutoRenewalMonth, enableAllowUpdateOnAutoRenewalMonth } = featureFlags;

  // Get cart config from master data
  const cartConfigData = useCartConfig();
  const [cartConfig] = cartConfigData;
  const renewalConfig = cartConfig?.accountProcess?.renewal;
  const memberTypeKey = AccountTypeCode[parseString(type)] as keyof CartAccountProcess;
  const skuConfig = renewalConfig?.[memberTypeKey as keyof CartAccountProcess];
  const skuDefaultKey = parseString(skuConfig?.default, AccountLifeCycleKitType.P_KIT);
  const defaultSku = parseString(skuConfig?.[skuDefaultKey]);

  // Configure for starter kits for listing
  const constructStarterKits = (skuConfig: DynamicObjectInterface) => {
    const starterKits: DynamicObjectInterface[] = [];
    if (skuConfig?.eKit) {
      starterKits.push({
        sku: parseString(skuConfig?.eKit),
        tncEnabled: true,
        productName: t('profile.AccountRenewal.AutoRenewal.selectKit.eKit'),
        selectedTitle: t(`profile.AccountRenewal.AutoRenewal.selectKit.selected.eKit.${parseString(type)}.title`),
        productDescription: t(`profile.AccountRenewal.AutoRenewal.selectKit.selected.eKit.${parseString(type)}.desc`),
        isEKit: true,
      });
    }
    starterKits.push({
      sku: parseString(skuConfig?.pKit),
      tncEnabled: false,
      productName: t('profile.AccountRenewal.AutoRenewal.selectKit.pKit'),
      selectedTitle: t(`profile.AccountRenewal.AutoRenewal.selectKit.selected.pKit.${parseString(type)}.title`),
      productDescription: t(`profile.AccountRenewal.AutoRenewal.selectKit.selected.pKit.${parseString(type)}.desc`),
      isEKit: false,
    });
    return starterKits;
  };

  const starterKits = constructStarterKits(skuConfig);

  const initFeeData = async () => await autoRenewalInitFeeData(skuConfig);

  const initAutoRenewalData = async (regId?: string, selectedSku?: string) => {
    const logger = Logger.getInstance(logObject('accountLifeCycleHook.ts', 'useAutoRenewal'));

    try {
      if (regId || !selectedFeeSku) {
        const skuData = await initFeeData();
        const { currency, totalAmount, formattedPrice } = skuData;
        const regIdObj = iff(Boolean(regId), { registrationId: regId }, {});

        dispatch(
          // Set default values
          updateAccountAutoRenewalData({
            ...accountAutoRenewalData,
            ...regIdObj,
            selectedKitSku: parseString(selectedSku),
            selectedFeeSku: skuConfig?.fee,
            changeKitSku: !selectedSku,
            checkoutProfile: null,
            currency: currency,
            totalAmount: totalAmount,
            formattedPrice: formattedPrice,
            qty: 1,
          }),
        );
      }
      return true;
    } catch (error: DynamicObjectInterface) {
      logger.error('Error initializing auto renewal data (product):', error);
      return false;
    }
  };

  const getExistingRegistration = async (registrationId?: string) =>
    await autoRenewalGetExistingRegistration(aboNumber, registrationId);

  const startAutoRenewalProcess = async (
    registrationId?: string,
    reuseRegistration = false,
  ): Promise<RegistrationResponse> => await autoRenewalStartProcess(aboNumber, registrationId, reuseRegistration);

  const transformToSubscription = (
    isEkit: boolean,
    isHomeDelivery: boolean,
    pickupLocationId?: string | null,
    selectedKitSku?: string,
    locale?: string,
  ): Subscription =>
    autoRenewalTransformToSubscription(isEkit, isHomeDelivery, pickupLocationId, selectedKitSku, locale);

  // reverse subscription
  const transformFromSubscription = (sub: Subscription) => autoRenewalTransformFromSubscription(sub);

  const getPaymentByRefId = async (refId: string): Promise<PaymentDetails | null> => {
    try {
      const payments = await apiErrorHandler(() => PaymentApi.getPayments(refId, refType));
      const [payment] = payments;
      return payment;
    } catch {
      // No handling required
      return null;
    }
  };

  const getOrCreatePayment = async (
    registrationId: string,
    orderAmount: Amount,
    transactions: Array<CreateTransactionRequest>,
    paymentId?: string,
  ): Promise<PaymentDetails> => {
    if (paymentId) {
      // get payment by paymentId
      return await PaymentApi.getPaymentById(paymentId);
    }

    // Temporary not get by regId
    // if (registrationId) {
    //   // To get payment details from the registration ID (getRegistration)
    //   const existingPaymentDetail = await getPaymentByRefId(registrationId);
    //   if (existingPaymentDetail) {
    //     return existingPaymentDetail;
    //   }
    // }

    // create new payment
    // TODO: change to constant after entire BusinessInformation moved to UI-COMPOSITE 'ARN_'
    const payload: CreatePaymentRequest = {
      refId: `ARN_${registrationId}`,
      refType: refType,
      amount: orderAmount,
      transactions: transactions,
    };
    return await PaymentApi.createPayment(payload);
  };

  const getProfile = async () => await apiErrorHandler(() => profileApi.getProfilesById(profileId));

  const getAccountEmail = async () => {
    const emailRes = await apiErrorHandler(() => profileApi.getAccountEmails(aboNumber, profileId, true));
    const { emailList } = emailRes;
    const [email] = parseArray(emailList);
    return email;
  };

  const getAccountPhone = async () => {
    const phoneRes = await apiErrorHandler(() => profileApi.getAccountPhones(aboNumber, profileId, true));
    const { phoneList } = phoneRes;
    const [phone] = parseArray(phoneList);
    return phone;
  };

  const getDefaultAddress = async () => await autoRenewalGetDefaultAddress(aboNumber, profileId, ContactType.Shipping);

  const validatePaymentDetails = (paymentDetails: PaymentDetails): boolean =>
    autoRenewalValidatePaymentDetails(paymentDetails);

  // error handling
  const commonErrorHandling = (error: DynamicObjectInterface, flowName: string, showError?: FunctionType) => {
    // this error for general or comregistrationas-internal-server-error
    const errorObject = {
      headline: 'profile.AccountRenewal.error.UnableToProcess',
      message: 'profile.AccountRenewal.error.invalid-registration', // default error message
    };

    const errorCode = handleAPIResponseError(error);
    if (errorCode !== ErrorConstants.REGISTRATION_INTERNAL_SERVER_ERROR) {
      errorObject.message = `profile.AccountRenewal.error.${flowName}.${errorCode}`;
    }

    showError?.(errorObject);

    return errorObject;
  };

  return {
    t,
    dispatch,

    // Session data
    session,
    isABO,
    profileId,
    aboNum: aboNumber,
    accountExpirationDate: accountExpirationDate ? parseDate(accountExpirationDate) : null,

    // Configs
    refType,
    defaultSku,
    starterKits,
    authorizationCharge,
    autoRenewProcessDate,
    reuseRegistration,
    allowInitiateFromCheckout,
    allowRenewOnRenewalMonth: enableAllowRenewOnAutoRenewalMonth,
    allowUpdateOnRenewalMonth: enableAllowUpdateOnAutoRenewalMonth,
    defaultPickupType,

    autoRenewSubscriptionId,
    autoRenewSubscriptionName,

    // Functions/data
    initFeeData,
    initAutoRenewalData,
    startAutoRenewalProcess,
    getExistingRegistration,
    getAccountAutoRenewal,
    getProfile,
    getAccountEmail,
    getAccountPhone,
    getDefaultAddress,
    getOrCreatePayment,
    validatePaymentDetails,
    transformToSubscription,
    transformFromSubscription,
    getPaymentByRefId, // consider to remove if no longer using
    commonErrorHandling,
  };
};

// This function is to export for Cart Team
export const useRenewalStatus = () => {
  const myAccountConfig = useMyAccountProfileConfig();
  const t = useTranslation();
  const siteLocale = useLocale();
  const siteCountryCode = siteCountry().toUpperCase();
  const { session } = useSession();
  const { type: accountType } = session;
  const apiErrorHandler = useGlobalErrorHandler();

  const featureFlags = useFeatureFlag();

  const getRenewalNudgeStatus = async (): Promise<RenewalNudgeType> => {
    const { aboNum } = session;
    if (!aboNum) {
      // for non-login user
      return {
        showRenewalNudge: false,
        isAutoRenewalEnabled: false,
        isAutoRenewalFailed: false,
        isLastGraceMonth: false,
        renewalNudgeMessage: '',
        canRenew: false,
        mustRenew: false,
        renewalStartDate: '',
      };
    }

    const accountRenewalStatus = await accountRenewalBannerStatus(
      session,
      myAccountConfig,
      featureFlags,
      siteLocale,
      null,
      undefined, // Not use cache for now, unless autorenewalorder status added to session
      apiErrorHandler,
    );
    const {
      isAutoRenewalEnabled,
      isAutoRenewalFailed,
      showRenewalNudge,
      mustRenew,
      canRenew,
      inactiveDate,
      expiryDate,
      isLastGraceMonth,
      renewalStartDate,
    } = accountRenewalStatus || {};

    const lastGracePeriodLabel = isLastGraceMonth ? '.lastMonth' : '';

    const messageConfig = {
      default: {
        message: t(`profile.AccountRenewal.RenewalNudge.defaultExpiryMessage.${siteCountryCode}`, {
          date: expiryDate,
        }),
        theme: Theme.PURPLE,
        alertTheme: Theme.PURPLE,
      },
      mustRenew: {
        message: t(
          `profile.AccountRenewal.RenewalNudge.expiredMessage.${siteCountryCode}.${accountType}${lastGracePeriodLabel}`,
          { date: inactiveDate },
        ),
        theme: isLastGraceMonth ? Theme.RED : Theme.ORANGE,
        alertTheme: isLastGraceMonth ? Theme.RED : Theme.WARNING,
      },
      autoRenewalEnabled: {
        message: t('profile.AccountRenewal.RenewalNudge.autoRenewalEnabledMessage'),
        theme: Theme.PURPLE,
        alertTheme: Theme.PURPLE,
      },
      autoRenewalFailed: {
        message: t('profile.AccountRenewal.RenewalNudge.autoRenewalFailedMessage'),
        theme: Theme.ORANGE,
        alertTheme: Theme.WARNING,
      },
    };

    // const nonAutoRenewalConfig = mustRenew ? messageConfig.mustRenew : messageConfig.default;
    // const autoRenewalConfig = isAutoRenewalFailed ? messageConfig.autoRenewalFailed : nonAutoRenewalConfig;
    // const config = isAutoRenewalEnabled ? autoRenewalConfig : nonAutoRenewalConfig;

    const config = mustRenew ? messageConfig.mustRenew : messageConfig.default;
    const { message, theme, alertTheme } = config;

    return {
      showRenewalNudge,
      isAutoRenewalEnabled,
      isAutoRenewalFailed,
      isLastGraceMonth,
      renewalNudgeMessage: message,
      renewalNudgeTheme: theme,
      alertMessageTheme: alertTheme,
      canRenew,
      mustRenew,
      inactiveDate,
      expiryDate,
      renewalStartDate,
    } as RenewalNudgeType;
  };

  return {
    getRenewalNudgeStatus,
  };
};
